// One search box for churches, places, languages, sacraments and days.
//
// Typing "spanish" offers the language filter; "pasadena" the city; "st
// monica" the parish; "confession" the sacrament; "sunday" the day. Each
// result is an action the caller applies, so the box is also the command
// line of the map. Ranking prefers a match at the start of a word.

import { DAY_NAMES } from './schedule.js';
import { LANGUAGE_NAMES } from './i18n.js';

const fold = (s) => (s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

const ABBREV = [[/\bst\.?\b/g, 'saint'], [/\bsts\.?\b/g, 'saints'], [/\bmt\.?\b/g, 'mount'], [/\bbl\.?\b/g, 'blessed'], [/\bour lady\b/g, 'our lady nuestra senora']];
function expand(s) { let t = fold(s); for (const [re, rep] of ABBREV) t = t.replace(re, rep); return t; }

export function buildIndex(data, t) {
  const churches = data.churches.map((c) => ({ c, key: expand(`${c.n} ${c.c || ''} ${c.a || ''}`) }));
  const cities = new Map();
  const regions = new Map();
  for (const c of data.churches) {
    if (c.c) cities.set(c.c, (cities.get(c.c) || 0) + 1);
    if (c.r) regions.set(c.r, (regions.get(c.r) || 0) + 1);
  }
  const places = [
    ...[...cities.entries()].map(([n, count]) => ({ type: 'city', value: n, label: n, count })),
    ...[...regions.entries()].map(([n, count]) => ({ type: 'region', value: n, label: n, count })),
    ...(data.places || []).map((p) => ({ type: 'nb', value: p.s, label: p.n, count: p.count, lng: p.lng, lat: p.lat })),
  ].map((p) => ({ ...p, key: fold(p.label) }));
  const langs = [...new Set([...(data.tenant.languageIndex || []), ...data.churches.flatMap((c) => c.l || [])])]
    .filter(Boolean).map((code) => ({ code, label: LANGUAGE_NAMES[code] || code, key: fold(`${LANGUAGE_NAMES[code] || ''} ${code} ${t('lang.' + code, '')}`) }));
  const facets = [
    { key: 'hc', words: 'confession confesion reconciliation penance', label: t('facet.hc') },
    { key: 'ha', words: 'adoration adoracion eucharistic blessed sacrament exposition', label: t('facet.ha') },
    { key: 'hp', words: 'perpetual adoration perpetua 24 hour', label: t('facet.hp') },
    { key: 'lm', words: 'latin mass tridentine extraordinary form misa en latin', label: t('facet.lm') },
    { key: 'sc', words: 'school escuela parochial', label: t('facet.sc') },
    { key: 'now', words: 'now next mass soon ahora proxima misa', label: t('facet.now') },
    { key: 'kind:c', words: 'cathedral catedral', label: t('kind.c') },
    { key: 'kind:b', words: 'basilica', label: t('kind.b') },
    { key: 'kind:s', words: 'shrine santuario', label: t('kind.s') },
    { key: 'kind:m', words: 'mission chapel mision capilla', label: t('kind.m') },
  ].map((f) => ({ ...f, key2: fold(`${f.words} ${f.label}`) }));
  const days = DAY_NAMES.map((n, i) => ({ day: i, label: t('day.' + i), key: fold(`${n} ${t('day.' + i)}`) }));
  return { churches, places, langs, facets, days, hasSchedules: !!data.tenant.schedules };
}

function score(key, q) {
  if (!q) return 0;
  const i = key.indexOf(q);
  if (i < 0) return 0;
  if (i === 0) return 3;
  return key[i - 1] === ' ' ? 2 : 1;
}

/** Groups: churches, places, languages, filters, days. At most `limit` per group. */
export function search(index, query, limit = 6) {
  const q = expand(query).trim();
  if (q.length < 2) return [];
  const words = q.split(/\s+/).filter(Boolean);
  const all = (key) => words.every((w) => key.includes(w));
  const groups = [];

  const langs = index.langs.filter((l) => score(l.key, q) > 0).slice(0, 3)
    .map((l) => ({ type: 'lang', code: l.code, label: l.label }));
  const facets = index.facets.filter((f) => f.key2.includes(q) || words.every((w) => f.key2.includes(w))).slice(0, 3)
    .map((f) => ({ type: 'facet', key: f.key, label: f.label }));
  const days = index.hasSchedules ? index.days.filter((d) => score(d.key, q) > 0).slice(0, 2)
    .map((d) => ({ type: 'day', day: d.day, label: d.label })) : [];
  if (langs.length || facets.length || days.length) groups.push({ type: 'filters', items: [...langs, ...facets, ...days] });

  const places = index.places.map((p) => ({ p, s: score(p.key, q) || (all(p.key) ? 1 : 0) })).filter((x) => x.s > 0)
    .sort((a, b) => b.s - a.s || b.p.count - a.p.count).slice(0, 4)
    .map(({ p }) => ({ type: 'place', placeType: p.type, value: p.value, label: p.label, count: p.count, lng: p.lng, lat: p.lat }));
  if (places.length) groups.push({ type: 'places', items: places });

  const churches = index.churches.map(({ c, key }) => ({ c, s: score(key, q) || (all(key) ? 1 : 0) })).filter((x) => x.s > 0)
    .sort((a, b) => b.s - a.s || a.c.n.localeCompare(b.c.n)).slice(0, limit)
    .map(({ c }) => ({ type: 'church', church: c, label: c.n, sub: [c.c, c.st].filter(Boolean).join(', ') }));
  if (churches.length) groups.push({ type: 'churches', items: churches });

  return groups;
}

/** The free-text predicate used when the query is left as a filter instead of picked as an action. */
export function textMatcher(index) {
  const byId = new Map(index.churches.map((x) => [x.c.i, x.key]));
  return (c, q) => {
    const key = byId.get(c.i); const t = expand(q).trim();
    if (!key || !t) return true;
    return t.split(/\s+/).every((w) => key.includes(w));
  };
}
